import { useState, useCallback } from 'react';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import { fg } from '@atlassian/jira-feature-gating';
import { fireTrackAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import {
	useMigrationIdManager,
	APP_TEAM_NAME,
	useExperienceTracker,
} from '@atlassian/jira-project-create-importer-common';
import { APP_IDENTIFIER } from '../../common/constants';
import {
	EXPERIENCES,
	startThirdPartyImportInteractionMetric,
} from '../../common/constants/metrics';
import { API_FLOW } from '../../common/types';
import { getSiteUrl } from '../../common/utils';
import { useAnalyticsAttributesContext } from '../../common/utils/analtics-attributes-context';
import { useThirdPartyClient } from '../../common/utils/third-party-clients';
import type {
	StartImportArgs,
	StartThirdPartyImportPayload,
	UseStartThirdPartyImport,
	UseStartThirdPartyImportProps,
} from './types';
import { getImportAdditionalProps } from './utils';

export const useStartThirdPartyImport = ({
	thirdParty,
	requestAuthenticationToken,
}: UseStartThirdPartyImportProps): UseStartThirdPartyImport => {
	const thirdPartyClient = useThirdPartyClient({
		thirdParty,
		requestAuthenticationToken,
	});
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [onExperienceStart, onExperienceSuccess, onExperienceFail] = useExperienceTracker(
		EXPERIENCES.START_THIRD_PARTY_IMPORT,
	);
	const [isLoading, setIsLoading] = useState(false);
	const [hasErrorOccurred, setHasErrorOccurred] = useState(false);

	const { storeMigrationId } = useMigrationIdManager();

	const analtyticsAttributes = useAnalyticsAttributesContext();

	const startThirdPartyImport = useCallback(
		async (args: StartImportArgs) => {
			const {
				projectData,
				selectedEntityId,
				selectedEntities,
				userEmails,
				thirdPartyConfigState,
				thirdPartyStatusMapping,
				importSettings,
				onSuccess,
				parentEntityId,
				isMultiEntity,
			} = args;

			setIsLoading(true);
			setHasErrorOccurred(false);
			startThirdPartyImportInteractionMetric.start();
			onExperienceStart();

			try {
				const additionalProps = thirdPartyConfigState
					? getImportAdditionalProps({
							state: thirdPartyConfigState,
							importSettings,
							thirdParty,
							statusMapping: thirdPartyStatusMapping,
						})
					: { additionalPayload: {}, additionalAttributes: {} };

				const payload: StartThirdPartyImportPayload = {
					projectData,
					jiraUrl: getSiteUrl(),
					userEmailMappings: userEmails,
					...additionalProps.additionalPayload,
				};

				if (!fg('importer_discovery_unify_event')) {
					fireTrackAnalytics(createAnalyticsEvent({}), 'thirdPartyImport started', {
						thirdParty,
						numberOfSelectedEntities: isMultiEntity ? selectedEntities?.length : 1,
						...additionalProps.additionalAttributes,
					});
				}

				const { migrationId } = await thirdPartyClient.startImport({
					entityId: isMultiEntity
						? (selectedEntities && selectedEntities[0].id) || ''
						: selectedEntityId,
					payload,
					...(fg('jwm_clickup_importer') && { parentEntityId }),
					isMultiEntity,
				});

				setIsLoading(false);
				storeMigrationId(projectData.key, migrationId);

				startThirdPartyImportInteractionMetric.stop();

				if (fg('importer_discovery_unify_event')) {
					fireTrackAnalytics(createAnalyticsEvent({}), 'import started', {
						migrationId,
						importerFlow: API_FLOW,
						thirdParty,
						numberOfSelectedEntities: isMultiEntity ? selectedEntities?.length : 1,
						...additionalProps.additionalAttributes,
					});
				}
				onExperienceSuccess({
					thirdParty: thirdParty || '',
					...analtyticsAttributes,
				});

				onSuccess(migrationId);
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				startThirdPartyImportInteractionMetric.stop();
				onExperienceFail(APP_IDENTIFIER, error, {
					thirdParty: thirdParty || '',
					...analtyticsAttributes,
				});
				fireErrorAnalytics({
					meta: {
						id: 'start-third-party-import-error',
						packageName: APP_IDENTIFIER,
						teamName: APP_TEAM_NAME,
					},
					sendToPrivacyUnsafeSplunk: true,
					error,
				});

				setHasErrorOccurred(true);
				setIsLoading(false);
			}
		},
		[
			onExperienceStart,
			thirdParty,
			createAnalyticsEvent,
			analtyticsAttributes,
			thirdPartyClient,
			storeMigrationId,
			onExperienceSuccess,
			onExperienceFail,
		],
	);

	return { isLoading, hasErrorOccurred, startThirdPartyImport };
};
